import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { waLink, monthLabel, currentMonth, rupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MessageCircle, Plus, Pencil, Send, Zap, History } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const CAT_META = {
  jadwal: { label: "Pengingat Jadwal", cls: "bg-sky-100 text-sky-700" },
  pembayaran: { label: "Pembayaran", cls: "bg-rose-100 text-rose-700" },
  konfirmasi: { label: "Konfirmasi", cls: "bg-emerald-100 text-emerald-700" },
  jurnal: { label: "Jurnal", cls: "bg-violet-100 text-violet-700" },
  absensi: { label: "Absensi", cls: "bg-amber-100 text-amber-700" },
  grup_harian: { label: "Grup WA Harian", cls: "bg-orange-100 text-orange-700" },
};

export default function WhatsApp() {
  const [templates, setTemplates] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendFor, setSendFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", category: "jadwal", body: "" });
  const [selStudent, setSelStudent] = useState("");
  const [settings, setSettings] = useState({});
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState("");

  const loadLogs = () => api.get("/wa/logs", { params: { limit: 30 } }).then((r) => setLogs(r.data));
  const load = () => {
    setLoading(true);
    Promise.all([api.get("/wa-templates"), api.get("/students", { params: { status: "aktif" } }), api.get("/settings")])
      .then(([t, s, st]) => { setTemplates(t.data); setStudents(s.data); setSettings(st.data); }).finally(() => setLoading(false));
    loadLogs();
  };
  useEffect(load, []);
  const auto = !!settings.fonnte_configured;

  const runJob = async (job) => {
    setBusy(job);
    try {
      const r = await api.post(`/wa/run/${job}`);
      if (r.data.skipped) toast.warning(r.data.skipped === "no_sessions" ? "Tidak ada jadwal les hari ini, notif grup tidak dikirim" : "Otomatisasi nonaktif, token / ID grup belum diisi");
      else if (job === "grup") toast[r.data.sent ? "success" : "error"](r.data.sent ? `Jadwal ${r.data.count} siswa terkirim ke grup WA` : `Gagal: ${r.data.reason}`);
      else toast.success(`Terkirim ke ${r.data.sent} wali`);
      loadLogs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const fill = (body, s) => {
    const sched = (s.schedule || [])[0] || {};
    const base = (settings.public_url || window.location.origin).replace(/\/$/, "");
    const portal = `${base}/portal/${s.portal_token || ""}`;
    return body
      .replace(/{link_portal}/g, portal).replace(/{link_jadwal}/g, portal).replace(/{link_tagihan}/g, `${portal}#tagihan`)
      .replace(/{link_kwitansi}/g, `${portal}#kwitansi`).replace(/{link_jurnal}/g, `${portal}#jurnal`).replace(/{link_kehadiran}/g, `${portal}#kehadiran`)
      .replace(/{link_jadwal_hari_ini}/g, `${base}/jadwal-hari-ini`)
      .replace(/{nama_siswa}/g, s.name)
      .replace(/{hari}/g, sched.day || "-")
      .replace(/{jam}/g, sched.time || "-")
      .replace(/{bulan}/g, monthLabel(currentMonth()))
      .replace(/{nominal}/g, Number(s.monthly_fee || 150000).toLocaleString("id-ID"))
      .replace(/{tanggal}/g, new Date().toLocaleDateString("id-ID"))
      .replace(/{materi}/g, "(isi materi)")
      .replace(/{pr}/g, "(isi PR)")
      .replace(/{bintang}/g, "⭐⭐⭐⭐⭐")
      .replace(/{catatan}/g, "(catatan guru)")
      .replace(/{status}/g, "Hadir");
  };

  const openSend = (t) => {
    if (t.category === "grup_harian") { setGroupFor(t); return; }
    setSendFor(t);
  };
  const [groupFor, setGroupFor] = useState(null);
  const sendGroup = async () => {
    setBusy("grup");
    try {
      const r = await api.post("/wa/run/grup");
      if (r.data.skipped === "no_sessions") toast.warning("Tidak ada jadwal les hari ini, pesan tidak dikirim");
      else if (r.data.skipped) toast.warning("Token Fonnte / ID grup belum diisi di Pengaturan");
      else toast[r.data.sent ? "success" : "error"](r.data.sent ? `Jadwal ${r.data.count} siswa terkirim ke grup WA` : `Gagal: ${r.data.reason}`);
      loadLogs(); setGroupFor(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const doSend = async (viaApi) => {
    const s = students.find((x) => x.id === selStudent);
    if (!s) return toast.error("Pilih siswa dulu");
    const msg = fill(sendFor.body, s);
    if (viaApi) {
      setBusy("send");
      try {
        await api.post("/wa/send", { student_id: s.id, message: msg, kind: sendFor.category });
        toast.success(`Pesan terkirim ke wali ${s.name} ✅`);
        loadLogs();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return; }
      finally { setBusy(""); }
    } else {
      window.open(waLink(s.wa, msg), "_blank");
    }
    setSendFor(null); setSelStudent("");
  };

  const saveTemplate = async () => {
    if (!form.title || !form.body) return toast.error("Judul & isi wajib diisi");
    try {
      if (editing?.id) await api.put(`/wa-templates/${editing.id}`, form);
      else await api.post("/wa-templates", form);
      toast.success("Template disimpan");
      setEditing(null); setForm({ title: "", category: "jadwal", body: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-6 animate-float-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Notifikasi WhatsApp</h1>
          <p className="text-slate-500 mt-1">Template pesan siap pakai · kirim otomatis via Fonnte atau buka WA Desktop</p>
        </div>
        <Button data-testid="add-template-btn" onClick={() => { setEditing({}); setForm({ title: "", category: "jadwal", body: "" }); }} className="rounded-xl bg-emerald-500 hover:bg-emerald-600">
          <Plus className="w-4 h-4 mr-1" /> Template Baru
        </Button>
      </div>

      <Card className="p-4 rounded-2xl border-emerald-100 bg-emerald-50/40 text-sm text-slate-600">
        💡 Placeholder tersedia: <code className="bg-white px-1.5 py-0.5 rounded">{"{nama_siswa}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{hari}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{jam}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{bulan}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{nominal}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{link_portal}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{link_jadwal}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{link_tagihan}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{link_kwitansi}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{link_jurnal}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{link_kehadiran}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{link_jadwal_hari_ini}"}</code> <code className="bg-white px-1.5 py-0.5 rounded">{"{daftar_siswa}"}</code>
      </Card>

      <Card className="p-5 rounded-2xl border-amber-100" data-testid="wa-auto-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${auto ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}><Zap className="w-5 h-5" /></div>
            <div>
              <h3 className="font-display font-bold text-slate-900">Kirim Otomatis (Fonnte)</h3>
              <p className="text-xs text-slate-500">{auto ? "Aktif · jadwal 07:00 WIB tiap hari, tagihan tgl 10 & 15" : <>Belum terhubung · <Link to="/pengaturan" className="text-emerald-600 underline">isi token Fonnte di Pengaturan</Link></>}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid="run-jadwal-btn" disabled={!auto || !!busy} onClick={() => runJob("jadwal")} className="rounded-xl">{busy === "jadwal" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kirim Pengingat Jadwal Hari Ini"}</Button>
            <Button size="sm" variant="outline" data-testid="run-tagihan-btn" disabled={!auto || !!busy} onClick={() => runJob("tagihan")} className="rounded-xl">{busy === "tagihan" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kirim Pengingat Tagihan"}</Button>
            <Button size="sm" variant="outline" data-testid="run-grup-btn" disabled={!auto || !!busy} onClick={() => runJob("grup")} className="rounded-xl">{busy === "grup" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kirim Jadwal ke Grup WA"}</Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map((t) => (
          <Card key={t.id} data-testid={`template-${t.id}`} className="p-5 rounded-2xl border-amber-100 hover:shadow-md transition-shadow flex flex-col">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-slate-900">{t.title}</h3>
              <Badge className={`rounded-full border-0 ${CAT_META[t.category]?.cls || "bg-slate-100"}`}>{CAT_META[t.category]?.label || t.category}</Badge>
            </div>
            <p className="text-sm text-slate-500 mt-2 whitespace-pre-wrap flex-1">{t.body}</p>
            <div className="flex gap-2 mt-4 pt-3 border-t border-amber-50">
              <Button size="sm" data-testid={`send-template-${t.id}`} onClick={() => openSend(t)} className="rounded-lg bg-emerald-500 hover:bg-emerald-600 h-8"><Send className="w-4 h-4 mr-1" /> Kirim</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(t); setForm({ title: t.title, category: t.category, body: t.body }); }} className="rounded-lg text-slate-600 h-8 ml-auto"><Pencil className="w-4 h-4" /></Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Group send dialog */}
      <Dialog open={!!groupFor} onOpenChange={(v) => !v && setGroupFor(null)}>
        <DialogContent className="rounded-2xl" data-testid="group-send-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Kirim ke Grup WA</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <div className="rounded-xl bg-orange-50 border border-orange-100 p-3">
              <div className="text-xs text-slate-500">Tujuan</div>
              <div className="font-semibold text-slate-800" data-testid="group-target">{settings.wa_group_id || "Belum diatur"}</div>
              {!settings.wa_group_id && <Link to="/pengaturan" className="text-xs text-orange-600 underline">Atur ID grup di Pengaturan</Link>}
            </div>
            <p className="text-xs text-slate-500">Pesan berisi daftar siswa yang les hari ini ({"{daftar_siswa}"}) dan tautan halaman Jadwal Hari Ini. Tidak dikirim jika tidak ada jadwal.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupFor(null)} className="rounded-xl">Batal</Button>
            <Button data-testid="confirm-send-group-btn" disabled={busy === "grup" || !auto || !settings.wa_group_id} onClick={sendGroup} className="rounded-xl bg-emerald-500 hover:bg-emerald-600">{busy === "grup" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" /> Kirim Sekarang</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={!!sendFor} onOpenChange={(v) => !v && setSendFor(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-xl">Kirim: {sendFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Pilih Siswa</Label>
              <Select value={selStudent} onValueChange={setSelStudent}>
                <SelectTrigger data-testid="send-student-select" className="mt-1 rounded-xl"><SelectValue placeholder="Pilih siswa" /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selStudent && <div className="p-3 rounded-xl bg-slate-50 text-sm text-slate-600 whitespace-pre-wrap max-h-48 overflow-y-auto">{fill(sendFor.body, students.find((s) => s.id === selStudent))}</div>}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setSendFor(null)} className="rounded-xl">Batal</Button>
            <Button variant="outline" data-testid="confirm-send-btn" onClick={() => doSend(false)} className="rounded-xl border-emerald-300 text-emerald-700"><MessageCircle className="w-4 h-4 mr-1" /> Buka WhatsApp</Button>
            {auto && <Button data-testid="confirm-send-api-btn" disabled={busy === "send"} onClick={() => doSend(true)} className="rounded-xl bg-emerald-500 hover:bg-emerald-600">{busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" /> Kirim Otomatis</>}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs */}
      <Card className="p-5 rounded-2xl border-amber-100" data-testid="wa-logs-card">
        <div className="flex items-center gap-2 mb-3"><History className="w-5 h-5 text-slate-500" /><h3 className="font-display font-bold text-slate-900">Riwayat Kirim Otomatis</h3></div>
        {logs.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Belum ada pesan yang dikirim lewat API.</p> : (
          <div className="space-y-2 max-h-80 overflow-y-auto gemma-scroll">
            {logs.map((l) => (
              <div key={l.id} data-testid={`wa-log-${l.id}`} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 text-sm">
                <span className={`mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 ${l.ok ? "bg-emerald-500" : "bg-rose-500"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap gap-x-2 items-center"><span className="font-semibold text-slate-800">{l.student_name}</span><span className="text-xs text-slate-400">{l.phone}</span><Badge className={`rounded-full border-0 text-[10px] ${CAT_META[l.kind]?.cls || "bg-slate-100"}`}>{CAT_META[l.kind]?.label || l.kind}</Badge><span className="text-[10px] text-slate-400">{l.source}</span></div>
                  <div className="text-xs text-slate-500 truncate">{l.ok ? l.message : `Gagal: ${l.reason}`}</div>
                </div>
                <span className="text-[10px] text-slate-400 shrink-0">{new Date(l.created_at).toLocaleString("id-ID")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing?.id ? "Edit Template" : "Template Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Judul</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div>
              <Label>Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CAT_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Isi Pesan</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} className="mt-1 rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl">Batal</Button>
            <Button data-testid="save-template-btn" onClick={saveTemplate} className="rounded-xl bg-emerald-500 hover:bg-emerald-600">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
